// src/components/NotificationBell.tsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import API_URL from "../config/api";

import "./NotificationBell.css";


// =====================================================
// TYPES
// =====================================================

interface Notification {
  id: number | string;

  user_id:
    number | string;

  notification_type:
    string;

  title:
    string;

  message:
    string;

  link:
    string | null;

  is_read:
    boolean;

  created_at:
    string;

  read_at:
    string | null;
}


interface NotificationsResponse {
  success:
    boolean;

  notifications?:
    Notification[];

  message?:
    string;
}


interface UnreadCountResponse {
  success:
    boolean;

  unreadCount?:
    number;

  message?:
    string;
}


// =====================================================
// DATE LABEL
// =====================================================

const formatNotificationTime = (
  value: string
) => {

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const difference =
    Date.now() -
    date.getTime();

  const minute =
    60 * 1000;

  const hour =
    60 * minute;

  const day =
    24 * hour;


  if (
    difference < minute
  ) {
    return "Just now";
  }


  if (
    difference < hour
  ) {

    const minutes =
      Math.floor(
        difference /
        minute
      );

    return `${minutes}m ago`;
  }


  if (
    difference < day
  ) {

    const hours =
      Math.floor(
        difference /
        hour
      );

    return `${hours}h ago`;
  }


  if (
    difference <
    7 * day
  ) {

    const days =
      Math.floor(
        difference /
        day
      );

    return `${days}d ago`;
  }


  return date.toLocaleDateString(
    undefined,
    {
      month:
        "short",

      day:
        "numeric",
    }
  );
};


// =====================================================
// NOTIFICATION BELL
// =====================================================

const NotificationBell:
React.FC = () => {

  const navigate =
    useNavigate();


  const containerRef =
    useRef<HTMLDivElement | null>(
      null
    );


  const [
    isOpen,
    setIsOpen,
  ] = useState(false);


  const [
    notifications,
    setNotifications,
  ] = useState<
    Notification[]
  >([]);


  const [
    unreadCount,
    setUnreadCount,
  ] = useState(0);


  const [
    isLoading,
    setIsLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  // ===================================================
  // AUTH TOKEN
  // ===================================================

  const getToken =
    () =>
      localStorage.getItem(
        "token"
      );


  // ===================================================
  // LOAD UNREAD COUNT
  // ===================================================

  const loadUnreadCount =
    useCallback(
      async () => {

        const token =
          getToken();

        if (!token) {

          setUnreadCount(
            0
          );

          return;
        }


        try {

          const response =
            await fetch(
              `${API_URL}/api/notifications/unread-count`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );


          if (!response.ok) {
            return;
          }


          const data:
          UnreadCountResponse =
            await response.json();


          if (
            data.success
          ) {

            setUnreadCount(
              Number(
                data.unreadCount ||
                0
              )
            );
          }

        } catch (error) {

          console.error(
            "Unable to load notification count:",
            error
          );
        }
      },
      []
    );


  // ===================================================
  // LOAD NOTIFICATIONS
  // ===================================================

  const loadNotifications =
    useCallback(
      async () => {

        const token =
          getToken();

        if (!token) {
          return;
        }


        setIsLoading(
          true
        );

        setError("");


        try {

          const response =
            await fetch(
              `${API_URL}/api/notifications?page=1&limit=20`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );


          const data:
          NotificationsResponse =
            await response.json();


          if (
            !response.ok ||
            !data.success
          ) {

            throw new Error(
              data.message ||
              "Unable to load notifications."
            );
          }


          setNotifications(
            data.notifications ||
            []
          );

        } catch (error) {

          console.error(
            "Unable to load notifications:",
            error
          );


          setError(
            error instanceof Error
              ? error.message
              : "Unable to load notifications."
          );

        } finally {

          setIsLoading(
            false
          );
        }
      },
      []
    );


  // ===================================================
  // INITIAL COUNT
  // ===================================================

  useEffect(
    () => {

      loadUnreadCount();

      /*
        Keep the badge reasonably fresh.

        Later we can replace this with a Socket.IO
        event for true real-time notifications.
      */

      const interval =
        window.setInterval(
          loadUnreadCount,
          30000
        );


      return () => {

        window.clearInterval(
          interval
        );
      };

    },
    [
      loadUnreadCount,
    ]
  );


  // ===================================================
  // CLOSE WHEN CLICKING OUTSIDE
  // ===================================================

  useEffect(
    () => {

      const handleOutsideClick =
        (
          event:
          MouseEvent
        ) => {

          if (
            containerRef.current &&
            !containerRef.current.contains(
              event.target as Node
            )
          ) {

            setIsOpen(
              false
            );
          }
        };


      document.addEventListener(
        "mousedown",
        handleOutsideClick
      );


      return () => {

        document.removeEventListener(
          "mousedown",
          handleOutsideClick
        );
      };

    },
    []
  );


  // ===================================================
  // TOGGLE DROPDOWN
  // ===================================================

  const toggleNotifications =
    async () => {

      const nextOpen =
        !isOpen;


      setIsOpen(
        nextOpen
      );


      if (
        nextOpen
      ) {

        await Promise.all([
          loadNotifications(),
          loadUnreadCount(),
        ]);
      }
    };


  // ===================================================
  // MARK ONE READ
  // ===================================================

  const markAsRead =
    async (
      notification:
      Notification
    ) => {

      if (
        notification.is_read
      ) {
        return true;
      }


      const token =
        getToken();

      if (!token) {
        return false;
      }


      try {

        const response =
          await fetch(
            `${API_URL}/api/notifications/${notification.id}/read`,
            {
              method:
                "PATCH",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        if (!response.ok) {
          return false;
        }


        setNotifications(
          (
            current
          ) =>
            current.map(
              (
                item
              ) =>

                String(
                  item.id
                ) ===
                String(
                  notification.id
                )

                  ? {
                      ...item,
                      is_read:
                        true,
                    }

                  : item
            )
        );


        setUnreadCount(
          (
            current
          ) =>
            Math.max(
              0,
              current - 1
            )
        );


        return true;

      } catch (error) {

        console.error(
          "Unable to mark notification as read:",
          error
        );

        return false;
      }
    };


  // ===================================================
  // OPEN NOTIFICATION
  // ===================================================

  const openNotification =
    async (
      notification:
      Notification
    ) => {

      await markAsRead(
        notification
      );


      setIsOpen(
        false
      );


      if (
        notification.link
      ) {

        navigate(
          notification.link
        );
      }
    };


  // ===================================================
  // MARK ALL READ
  // ===================================================

  const markAllAsRead =
    async () => {

      if (
        unreadCount === 0
      ) {
        return;
      }


      const token =
        getToken();

      if (!token) {
        return;
      }


      try {

        const response =
          await fetch(
            `${API_URL}/api/notifications/read-all`,
            {
              method:
                "PATCH",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        if (!response.ok) {
          return;
        }


        setNotifications(
          (
            current
          ) =>
            current.map(
              (
                notification
              ) => ({
                ...notification,
                is_read:
                  true,
              })
            )
        );


        setUnreadCount(
          0
        );

      } catch (error) {

        console.error(
          "Unable to mark notifications as read:",
          error
        );
      }
    };


  // ===================================================
  // UI
  // ===================================================

  return (

    <div
      className="notification-bell"
      ref={
        containerRef
      }
    >

      {/* ============================================= */}
      {/* BELL BUTTON                                   */}
      {/* ============================================= */}

      <button
        type="button"
        className={
          `notification-bell-button ${
            isOpen
              ? "notification-bell-button-active"
              : ""
          }`
        }
        onClick={
          toggleNotifications
        }
        aria-label={
          unreadCount > 0
            ? `${unreadCount} unread notifications`
            : "Notifications"
        }
        aria-expanded={
          isOpen
        }
      >

        <span
          className="notification-bell-icon"
          aria-hidden="true"
        >
          ♢
        </span>


        {unreadCount > 0 && (

          <span
            className="notification-badge"
          >
            {
              unreadCount > 99
                ? "99+"
                : unreadCount
            }
          </span>

        )}

      </button>


      {/* ============================================= */}
      {/* DROPDOWN                                      */}
      {/* ============================================= */}

      {isOpen && (

        <div
          className="notification-dropdown"
        >

          {/* HEADER */}

          <div
            className="notification-dropdown-header"
          >

            <div>

              <span>
                P-ARENA
              </span>

              <h3>
                Notifications
              </h3>

            </div>


            {unreadCount > 0 && (

              <button
                type="button"
                onClick={
                  markAllAsRead
                }
                className="notification-mark-all"
              >
                Mark all read
              </button>

            )}

          </div>


          {/* BODY */}

          <div
            className="notification-dropdown-body"
          >

            {isLoading ? (

              <div
                className="notification-state"
              >
                <div
                  className="notification-loader"
                />

                <p>
                  Loading notifications...
                </p>
              </div>

            ) : error ? (

              <div
                className="notification-state notification-error"
              >
                <strong>
                  Unable to load
                </strong>

                <p>
                  {error}
                </p>

                <button
                  type="button"
                  onClick={
                    loadNotifications
                  }
                >
                  Try Again
                </button>
              </div>

            ) : notifications.length === 0 ? (

              <div
                className="notification-state"
              >

                <div
                  className="notification-empty-symbol"
                >
                  ♠
                </div>

                <strong>
                  You're all caught up
                </strong>

                <p>
                  New P-ARENA updates will
                  appear here.
                </p>

              </div>

            ) : (

              notifications.map(
                (
                  notification
                ) => (

                  <button
                    type="button"
                    key={
                      notification.id
                    }
                    className={
                      `notification-item ${
                        !notification.is_read
                          ? "notification-item-unread"
                          : ""
                      }`
                    }
                    onClick={() =>
                      openNotification(
                        notification
                      )
                    }
                  >

                    <span
                      className="notification-item-symbol"
                      aria-hidden="true"
                    >
                      {
                        notification.notification_type ===
                        "support_reply"
                          ? "♦"
                          : "♠"
                      }
                    </span>


                    <span
                      className="notification-item-content"
                    >

                      <span
                        className="notification-item-title"
                      >
                        {
                          notification.title
                        }
                      </span>


                      <span
                        className="notification-item-message"
                      >
                        {
                          notification.message
                        }
                      </span>


                      <span
                        className="notification-item-time"
                      >
                        {
                          formatNotificationTime(
                            notification.created_at
                          )
                        }
                      </span>

                    </span>


                    {!notification.is_read && (

                      <span
                        className="notification-unread-dot"
                        aria-label="Unread"
                      />

                    )}

                  </button>

                )
              )

            )}

          </div>

        </div>

      )}

    </div>
  );
};


export default NotificationBell;
